import { Box, Tooltip } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import api from '../../services/api';

interface HealthCheckResult {
  status: string;
  message?: string;
}

interface HealthResponse {
  status: string;
  checks?: Record<string, HealthCheckResult>;
}

type HealthStatus = 'loading' | 'healthy' | 'degraded' | 'unhealthy' | 'error';

const POLL_INTERVAL_MS = 60000; // 60 seconds

const AdminHealthIndicator = () => {
  const [status, setStatus] = useState<HealthStatus>('loading');
  const [lastError, setLastError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;

    const fetchStatus = async () => {
      try {
        const res = await api.get<HealthResponse>('/health/ready');
        if (cancelled) return;

        const raw = (res.data?.status || '').toLowerCase();
        let mapped: HealthStatus;
        if (raw === 'healthy') mapped = 'healthy';
        else if (raw === 'degraded') mapped = 'degraded';
        else mapped = 'unhealthy';

        setStatus(mapped);
        setLastError(null);
      } catch (err) {
        if (cancelled) return;
        // On error keep previous status if we already had one, otherwise mark as error
        setStatus((prev) => (prev === 'loading' ? 'error' : prev));
        setLastError('Nepodařilo se ověřit stav serveru');
      } finally {
        if (!cancelled) {
          timer = window.setTimeout(fetchStatus, POLL_INTERVAL_MS);
        }
      }
    };

    fetchStatus();

    return () => {
      cancelled = true;
      if (timer) {
        window.clearTimeout(timer);
      }
    };
  }, []);

  let color: string;
  switch (status) {
    case 'healthy':
      color = 'green.400';
      break;
    case 'degraded':
      color = 'orange.400';
      break;
    case 'unhealthy':
      color = 'red.400';
      break;
    case 'loading':
      color = 'yellow.300';
      break;
    case 'error':
    default:
      color = 'gray.400';
      break;
  }

  const label: string = (() => {
    if (status === 'loading') return 'Ověřuji stav serveru…';
    if (status === 'healthy') return 'Server v pořádku';
    if (status === 'degraded') return 'Server běží, ale v omezeném režimu';
    if (status === 'unhealthy') return 'Server není připraven (zdravotní kontrola selhala)';
    if (status === 'error') return lastError || 'Nepodařilo se ověřit stav serveru';
    return 'Stav serveru není známý';
  })();

  return (
    <Tooltip label={label} hasArrow>
      <Box
        as="span"
        w="10px"
        h="10px"
        borderRadius="full"
        bg={color}
        boxShadow="0 0 0 2px rgba(255, 255, 255, 0.6)"
      />
    </Tooltip>
  );
};

export default AdminHealthIndicator;
